import { OnboardingDetailsRole } from '@nemuru-eng/js-api-client';
import { useMemo } from 'react';

import { useCheckoutSessionContext } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/_shared/contexts/CheckoutSessionContext';
import { LoanPaymentMethodDetailEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/_shared/entities/PaymentMethodDetail/LoanPaymentMethodDetailEntity';
import { useCheckoutSessionAutoCart } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/hooks/useCheckoutSessionAutoCart';

export const usePaymentMethodEditActions = () => {
  const { paymentMethodDetailEntities, selectedPaymentMethodType, checkoutSessionEntity } = useCheckoutSessionContext();

  const {
    checkoutSession,
    isUserAllowedToEditVehicleData,
    canShowEditVehicleDataButton,
    isEditVehicleDataButtonDisabled,
    areFinancingConditionsEditable,
    refreshCheckoutSessionAndPaymentMethodEntities,
  } = useCheckoutSessionAutoCart();

  const activePaymentMethodDetailEntity = useMemo(() => {
    if (!selectedPaymentMethodType) {
      return undefined;
    }

    return paymentMethodDetailEntities.find(
      (paymentMethodDetailEntity) => paymentMethodDetailEntity.type === selectedPaymentMethodType,
    );
  }, [paymentMethodDetailEntities, selectedPaymentMethodType]);

  const loanPaymentMethodDetailEntity =
    activePaymentMethodDetailEntity instanceof LoanPaymentMethodDetailEntity
      ? activePaymentMethodDetailEntity
      : undefined;

  const isAuto = !!loanPaymentMethodDetailEntity?.isAuto();
  const showAutoActions = isAuto;
  const showLoanActions = !!loanPaymentMethodDetailEntity;

  const isEditVehicleDataDisabled =
    !isUserAllowedToEditVehicleData || !canShowEditVehicleDataButton || isEditVehicleDataButtonDisabled;

  const isEditFinancingDetailsDisabled =
    !areFinancingConditionsEditable || !isUserAllowedToEditVehicleData || !canShowEditVehicleDataButton;

  const isEditBorrowerDisabled = !loanPaymentMethodDetailEntity?.allowEditBorrower();

  const isEditCoBorrowerDisabled = !loanPaymentMethodDetailEntity?.allowEditCoBorrower();

  const hasCoborrower = loanPaymentMethodDetailEntity?.hasCoborrower();

  const canAddCoborrower = (() => {
    if (!loanPaymentMethodDetailEntity) {
      return false;
    }

    const loan = loanPaymentMethodDetailEntity.loan;
    if (!loan || !loanPaymentMethodDetailEntity.paymentIntentId || !checkoutSessionEntity) {
      return false;
    }

    const selectedRole =
      loan.onboardingDetails.length < 3
        ? loan.onboardingDetails.some((borrower) => borrower.role === OnboardingDetailsRole.CO_BORROWER)
          ? OnboardingDetailsRole.GUARANTOR
          : OnboardingDetailsRole.CO_BORROWER
        : undefined;

    return !!selectedRole && loanPaymentMethodDetailEntity.allowAddCoborrower();
  })();

  const isAddCoborrowerDisabled = !canAddCoborrower;
  const isMerchantPos = loanPaymentMethodDetailEntity?.isMerchantPos() ?? false;

  return {
    showAutoActions,
    showLoanActions,
    isAuto,
    loanPaymentMethodDetailEntity,
    checkoutSession,
    checkoutSessionEntity,
    isEditVehicleDataDisabled,
    isEditFinancingDetailsDisabled,
    isEditBorrowerDisabled,
    isEditCoBorrowerDisabled,
    isAddCoborrowerDisabled,
    hasCoborrower,
    refreshCheckoutSessionAndPaymentMethodEntities,
    isMerchantPos,
  };
};
